import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Download, FileText, Loader2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const LeaveLedgerDialog = ({ open, onOpenChange, employeeId, year }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!open || !employeeId) return;
    setLoading(true);
    axios.get(`${API}/api/leaves/ledger/${employeeId}?year=${year}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => toast({ title: 'Could not load ledger', variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [open, employeeId, year]); // eslint-disable-line

  const download = async (fmt) => {
    try {
      const res = await axios.get(`${API}/api/leaves/ledger/${employeeId}/export?format=${fmt}&year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `leave_ledger_${data?.employee_code || employeeId}_${year}.${fmt === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast({ title: 'Download failed', variant: 'destructive' }); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="leave-ledger-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-sky-600" />
            Leave Ledger {data ? `— ${data.employee_name} · ${year}` : ''}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-gray-500"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : data ? (
          <>
            <div className="flex items-center justify-between text-sm bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
              <span>Opening: <b>{data.opening_balance}</b> days</span>
              <span>Closing: <b>{data.closing_balance}</b> days</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => download('excel')} data-testid="ledger-export-excel">
                  <Download className="h-3.5 w-3.5 mr-1" /> Excel
                </Button>
                <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => download('pdf')} data-testid="ledger-export-pdf">
                  <Download className="h-3.5 w-3.5 mr-1" /> PDF
                </Button>
              </div>
            </div>
            <div className="overflow-auto mt-3 border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left text-xs text-gray-500">
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2 text-center">Days</th>
                    <th className="px-3 py-2 text-center">Annual Δ</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i} className={`border-t ${r.type === 'opening' ? 'bg-emerald-50/50 font-medium' : ''}`} data-testid={`ledger-row-${i}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{r.date}{r.end_date && r.end_date !== r.date ? ` → ${r.end_date}` : ''}</td>
                      <td className="px-3 py-2">{r.type === 'opening' ? 'Opening balance' : r.description}
                        {r.type === 'partial_unpaid' && r.paid_days != null && (
                          <span className="text-[10px] text-gray-400 block">paid {r.paid_days} · unpaid {r.unpaid_days}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center text-gray-600">{r.days ?? '—'}</td>
                      <td className={`px-3 py-2 text-center font-medium ${!r.affects_annual ? 'text-gray-300' : r.annual_change < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {!r.affects_annual ? '—' : (r.annual_change > 0 ? `+${r.annual_change}` : r.annual_change)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">{r.balance_after}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-gray-400 mt-2">Balance reflects accruals up to each leave's date. "Annual Δ" is the leave's own deduction from the annual pool. Non-annual leaves (sick, PH, mgmt-unpaid) don't affect the annual balance.</p>
          </>
        ) : (
          <p className="py-10 text-center text-gray-400">No data.</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeaveLedgerDialog;
